// Shared energy slider lives in src/services/energy.ts deliberately.
import { ENERGY_MAX, ENERGY_REF_HI, clampEnergy } from '../../../services/energy';
import { hash01, hashU32 } from './hash';
import { BRICK_PROFILE, allowsBackFace } from './profiles';
import {
  STATE_RANK,
  asTriple,
  escalateState,
  resolveWeapon,
  type BrickState,
  type DamageMaterialProfile,
  type LedgerStats,
  type StampParams,
  type WeaponCrackSpec,
  type WeaponKind,
} from './types';
import {
  closestBrickIndex,
  jointMid,
  type JointKind,
  type WallGrid,
} from './wallGrid';

export const MAX_POCKS = 3;
/** Brick default. Profiles may override via `bulletRemoveEnergy`. */
export const BULLET_REMOVE_ENERGY = BRICK_PROFILE.bulletRemoveEnergy;

export interface LedgerBrick {
  index: number;
  state: BrickState;
  integrity: number;
  seed: number;
  pockCount: number;
  pockU: [number, number, number];
  pockV: [number, number, number];
  pushIn: number;
  atlasVariant: number;
  atlasRot: number;
  atlasMirror: number;
  cornerMask: number;
  edgeDarken: number;
  throughCrack: number;
  throughAngle: number;
  throughAngle2: number;
  backSpall: number;
  backScorch: number;
}

export interface LedgerJoint {
  id: number;
  cracked: boolean;
  crackDepth: number;
}

export interface CrackPath {
  joints: number[];
  kinds: JointKind[];
}

export interface StampResult {
  weapon: WeaponKind;
  energy: number;
  r0: number;
  r1: number;
  r2: number;
  removedDelta: number;
  newPaths: number;
}

export interface DamageLedger {
  readonly grid: WallGrid;
  readonly bricks: readonly LedgerBrick[];
  readonly joints: readonly LedgerJoint[];
  stamp(input: StampParams, profile?: DamageMaterialProfile): StampResult;
  reset(): void;
  getStats(): LedgerStats;
  getCrackPaths(): readonly CrackPath[];
  snapshot(): string;
}

function clamp01(x: number): number {
  if (!Number.isFinite(x)) return 0;
  return Math.min(1, Math.max(0, x));
}

function faceUv(brickX: number, brickY: number, halfL: number, halfH: number, px: number, py: number): {
  u: number;
  v: number;
} {
  const u = clamp01((px - (brickX - halfL)) / (halfL * 2));
  const v = clamp01((py - (brickY - halfH)) / (halfH * 2));
  return {
    u: Math.min(0.88, Math.max(0.12, u)),
    v: Math.min(0.88, Math.max(0.12, v)),
  };
}

function applyIntegrity(brick: LedgerBrick, loss: number): void {
  const amt = clamp01(loss);
  brick.integrity = clamp01(brick.integrity * (1 - amt));
}

function escalate(brick: LedgerBrick, next: BrickState): boolean {
  const prev = brick.state;
  brick.state = escalateState(brick.state, next);
  return brick.state !== prev;
}

/**
 * Spatial scale for stamp radii. 1.0 at energy 1, 1.45 at energy 2,
 * sqrt-extends above 2 so 5.0 is a large breach not a linear monster.
 */
export function energyRadiusScale(e: number): number {
  const x = clampEnergy(e);
  if (x <= ENERGY_REF_HI) return 0.55 + 0.45 * x;
  return 1.45 + 0.28 * Math.sqrt((x - ENERGY_REF_HI) / (ENERGY_MAX - ENERGY_REF_HI));
}

export function stampRadii(
  energy: number,
  weapon: WeaponKind,
  profile: DamageMaterialProfile,
): { r0: number; r1: number; r2: number } {
  const s = energyRadiusScale(energy);
  const r = profile.radii;
  const coreK = profile.coreK;
  const rimK = profile.rimK;
  const spallK = profile.spallK;
  if (weapon === 'missile') {
    return {
      r0: r.core * s * r.missileCoreMul * coreK,
      r1: r.rim * s * r.missileRimMul * rimK,
      r2: r.missileSpall * s * r.missileSpallMul * spallK,
    };
  }
  return {
    r0: r.core * s * coreK,
    r1: r.rim * s * rimK,
    r2: r.spall * s * spallK,
  };
}

function countMergedZones(bricks: LedgerBrick[], neighbors: number[][]): number {
  const seen = new Uint8Array(bricks.length);
  let zones = 0;
  const stack: number[] = [];
  for (let i = 0; i < bricks.length; i += 1) {
    if (bricks[i].state !== 'removed' || seen[i]) continue;
    zones += 1;
    stack.push(i);
    seen[i] = 1;
    while (stack.length) {
      const cur = stack.pop() as number;
      const adj = neighbors[cur];
      for (let k = 0; k < adj.length; k += 1) {
        const n = adj[k];
        if (seen[n] || bricks[n].state !== 'removed') continue;
        seen[n] = 1;
        stack.push(n);
      }
    }
  }
  return zones;
}

function makeBrick(index: number, seed: number): LedgerBrick {
  return {
    index,
    state: 'intact',
    integrity: 1,
    seed,
    pockCount: 0,
    pockU: [0, 0, 0],
    pockV: [0, 0, 0],
    pushIn: 0,
    atlasVariant: seed % 16,
    atlasRot: (hashU32(seed ^ 0x51ed) % 4) * 90,
    atlasMirror: hashU32(seed ^ 0x2c1b) % 4,
    cornerMask: 0,
    edgeDarken: 0,
    throughCrack: 0,
    throughAngle: hash01(seed, 11) * Math.PI,
    throughAngle2: hash01(seed, 29) * Math.PI,
    backSpall: 0,
    backScorch: 0,
  };
}

export function createDamageLedger(grid: WallGrid): DamageLedger {
  const bricks: LedgerBrick[] = grid.bricks.map((b) => makeBrick(b.index, b.seed));
  const joints: LedgerJoint[] = grid.joints.map((j) => ({
    id: j.id,
    cracked: false,
    crackDepth: 0,
  }));
  const paths: CrackPath[] = [];
  let stamps = 0;

  const rederiveRim = (): void => {
    for (let i = 0; i < bricks.length; i += 1) {
      const brick = bricks[i];
      if (brick.state === 'removed') continue;
      const adj = grid.neighbors[i];
      let nextToHole = false;
      for (let k = 0; k < adj.length; k += 1) {
        if (bricks[adj[k]].state === 'removed') {
          nextToHole = true;
          break;
        }
      }
      if (!nextToHole) continue;
      escalate(brick, 'rim');
      if (brick.pushIn <= 0) {
        const t = hash01(brick.seed, 71);
        brick.pushIn = 0.008 + t * 0.014;
      }
      if (brick.cornerMask === 0) {
        brick.cornerMask = 1 + (brick.seed % 15);
      }
    }
  };

  const markJointCracked = (id: number, depth: number, crackLoss: number): void => {
    const j = joints[id];
    j.cracked = true;
    j.crackDepth = Math.max(j.crackDepth, depth);
    const def = grid.joints[id];
    const pair = [def.a, def.b];
    for (const bi of pair) {
      const brick = bricks[bi];
      brick.edgeDarken = Math.min(1, brick.edgeDarken + 0.35);
      if (brick.state === 'removed' || brick.state === 'rim') continue;
      escalate(brick, 'cracked');
      applyIntegrity(brick, crackLoss);
    }
  };

  const walkPath = (
    start: number,
    maxLen: number,
    pathSeed: number,
    ix: number,
    iy: number,
    branchBudget: number,
    spec: WeaponCrackSpec,
    crackLoss: number,
  ): CrackPath => {
    const seq: number[] = [];
    const kinds: JointKind[] = [];
    const used = new Set<number>();
    let current = start;
    let lastKind: JointKind | null = null;

    for (let step = 0; step < maxLen; step += 1) {
      if (used.has(current)) break;
      used.add(current);
      seq.push(current);
      const def = grid.joints[current];
      kinds.push(def.kind);
      markJointCracked(current, 0.55 + hash01(pathSeed, step) * 0.45, crackLoss);
      lastKind = def.kind;

      const mid = jointMid(def);
      const cands = grid.jointAdj[current];
      let best = -1;
      let bestScore = -1e9;
      let second = -1;
      let secondScore = -1e9;
      for (let c = 0; c < cands.length; c += 1) {
        const id = cands[c];
        if (used.has(id)) continue;
        const jd = grid.joints[id];
        const aState = bricks[jd.a].state;
        const bState = bricks[jd.b].state;
        if (aState === 'removed' && bState === 'removed') continue;
        const jm = jointMid(jd);
        const dist = Math.hypot(jm.x - ix, jm.y - iy);
        const curDist = Math.hypot(mid.x - ix, mid.y - iy);
        const outward = dist - curDist;
        if (outward < -0.012) continue;
        const stair = lastKind && jd.kind !== lastKind ? spec.stairBias : 0.05;
        const h = hash01(pathSeed, step, id);
        const score = outward * 6 + stair + h * 0.45 + (joints[id].cracked ? -0.8 : 0);
        if (score > bestScore) {
          second = best;
          secondScore = bestScore;
          best = id;
          bestScore = score;
        } else if (score > secondScore) {
          second = id;
          secondScore = score;
        }
      }
      if (best < 0) break;

      if (
        branchBudget > 0
        && second >= 0
        && hash01(pathSeed, step, 97) < spec.branchChance
      ) {
        const branchLen = Math.max(2, Math.floor(maxLen * 0.45));
        const branch = walkPath(
          second,
          branchLen,
          hashU32(pathSeed ^ (step + 3) * 131),
          ix,
          iy,
          0,
          spec,
          crackLoss,
        );
        if (branch.joints.length > 0) paths.push(branch);
        branchBudget -= 1;
      }
      current = best;
    }

    return { joints: seq, kinds };
  };

  const growCracks = (
    energy: number,
    ix: number,
    iy: number,
    stampIndex: number,
    profile: DamageMaterialProfile,
    weapon: WeaponKind,
  ): number => {
    const wc = profile.weaponCrack[weapon];
    if (!wc || wc.mode !== 'joint-graph') return 0;
    const nWanted = Math.max(
      wc.minCount,
      Math.min(wc.maxCount, Math.round((3 + energy * 1.2) * profile.crackCountK * wc.countK)),
    );
    const maxLen = Math.max(
      3,
      Math.round((5 + energy * 6) * profile.crackLengthK * wc.lengthK),
    );
    const starts: { id: number; score: number }[] = [];
    for (let i = 0; i < bricks.length; i += 1) {
      if (bricks[i].state !== 'rim') continue;
      const list = grid.brickJoints[i];
      for (let k = 0; k < list.length; k += 1) {
        const id = list[k];
        const def = grid.joints[id];
        const other = def.a === i ? def.b : def.a;
        if (bricks[other].state === 'removed') continue;
        const mid = jointMid(def);
        const dist = Math.hypot(mid.x - ix, mid.y - iy);
        starts.push({ id, score: dist + hash01(stampIndex, id, bricks[i].seed) * 0.05 });
      }
    }
    starts.sort((a, b) => b.score - a.score);
    const picked: number[] = [];
    for (const s of starts) {
      if (picked.length >= nWanted) break;
      if (picked.some((id) => id === s.id)) continue;
      picked.push(s.id);
    }
    let grown = 0;
    for (let p = 0; p < picked.length; p += 1) {
      const pathSeed = hashU32(stampIndex * 7919 + picked[p] * 13 + p * 97);
      const path = walkPath(
        picked[p],
        maxLen,
        pathSeed,
        ix,
        iy,
        1,
        wc,
        profile.integrity.crack,
      );
      if (path.joints.length > 0) {
        paths.push(path);
        grown += 1;
      }
    }
    return grown;
  };

  const applyBullet = (
    ix: number,
    iy: number,
    energy: number,
    profile: DamageMaterialProfile,
  ): StampResult => {
    const idx = closestBrickIndex(grid, ix, iy);
    const place = grid.bricks[idx];
    const brick = bricks[idx];
    const reach = Math.hypot(place.x - ix, place.y - iy);
    const maxReach = Math.hypot(grid.spec.brickLength, grid.spec.brickHeight) * 0.85;
    if (reach > maxReach) {
      return {
        weapon: 'bullet',
        energy,
        r0: 0,
        r1: 0,
        r2: 0,
        removedDelta: 0,
        newPaths: 0,
      };
    }
    const uv = faceUv(
      place.x,
      place.y,
      grid.spec.brickLength * 0.5,
      grid.spec.brickHeight * 0.5,
      ix,
      iy,
    );
    const pockCap = Math.min(MAX_POCKS, profile.pockEscalate);
    if (brick.pockCount < pockCap) {
      brick.pockU[brick.pockCount] = uv.u;
      brick.pockV[brick.pockCount] = uv.v;
      brick.pockCount += 1;
    }
    brick.throughCrack = 1;
    applyIntegrity(brick, profile.integrity.pock);
    if (brick.pockCount >= pockCap && brick.state === 'intact') {
      escalate(brick, 'spalled');
      applyIntegrity(brick, profile.integrity.spall * 0.5);
    }
    let removedDelta = 0;
    if (energy >= profile.bulletRemoveEnergy && brick.state !== 'removed') {
      brick.state = 'removed';
      brick.integrity = 0;
      brick.pushIn = 0;
      removedDelta = 1;
      // Punched-through unit: no rim ring, no joint cracks.
    }
    return {
      weapon: 'bullet',
      energy,
      r0: 0,
      r1: 0,
      r2: 0,
      removedDelta,
      newPaths: 0,
    };
  };

  const applyBlast = (
    ix: number,
    iy: number,
    energy: number,
    weapon: WeaponKind,
    stampIndex: number,
    profile: DamageMaterialProfile,
  ): StampResult => {
    const { r0, r1, r2 } = stampRadii(energy, weapon, profile);
    const shallow = weapon === 'missile';
    let removedDelta = 0;

    for (let i = 0; i < bricks.length; i += 1) {
      const place = grid.bricks[i];
      const brick = bricks[i];
      const d = Math.hypot(place.x - ix, place.y - iy);
      if (d <= r0) {
        if (brick.state !== 'removed') removedDelta += 1;
        brick.state = 'removed';
        brick.integrity = 0;
        brick.pushIn = 0;
        continue;
      }
      if (d <= r1) {
        escalate(brick, 'rim');
        applyIntegrity(brick, profile.integrity.rim);
        if (brick.pushIn <= 0) {
          const t = hash01(brick.seed, stampIndex, 3);
          const lo = shallow ? profile.rimPushIn.missileLo : profile.rimPushIn.lo;
          const hi = shallow ? profile.rimPushIn.missileHi : profile.rimPushIn.hi;
          brick.pushIn = lo + t * (hi - lo);
        }
        if (brick.cornerMask === 0) brick.cornerMask = 1 + ((brick.seed + stampIndex) % 15);
        continue;
      }
      if (d <= r2 && brick.state !== 'removed' && brick.state !== 'rim') {
        const t = (d - r1) / Math.max(1e-6, r2 - r1);
        const p = (shallow ? profile.spallProb.missile : profile.spallProb.cannon) * (1 - t);
        if (hash01(brick.seed, stampIndex, 19) < p) {
          escalate(brick, 'spalled');
          applyIntegrity(brick, profile.integrity.spall);
        }
      }
    }

    rederiveRim();
    const newPaths = growCracks(energy, ix, iy, stampIndex, profile, weapon);
    rederiveRim();
    applyBackMirror(ix, iy, energy, weapon, stampIndex, profile, r1);
    return { weapon, energy, r0, r1, r2, removedDelta, newPaths };
  };

  const applyBackMirror = (
    ix: number,
    iy: number,
    energy: number,
    weapon: WeaponKind,
    stampIndex: number,
    profile: DamageMaterialProfile,
    r1: number,
  ): void => {
    if (weapon !== 'missile') return;
    if (!allowsBackFace(profile, grid.spec.brickDepth, 1)) return;
    const reach = r1 * 1.35;
    const scale = profile.backSpallScale;
    for (let i = 0; i < bricks.length; i += 1) {
      const brick = bricks[i];
      if (brick.state === 'removed') continue;
      const place = grid.bricks[i];
      const d = Math.hypot(place.x - ix, place.y - iy);
      if (d > reach) continue;
      const t = 1 - d / Math.max(1e-6, reach);
      const jitter = 0.55 + hash01(brick.seed, stampIndex, 41) * 0.45;
      const amt = t * scale * jitter * Math.min(1, 0.55 + energy * 0.18);
      brick.backSpall = Math.max(brick.backSpall, amt);
      brick.backScorch = Math.max(brick.backScorch, amt * 0.88);
    }
  };

  const stamp = (input: StampParams, profileArg?: DamageMaterialProfile): StampResult => {
    const energy = clampEnergy(input.energy);
    const intensity = clamp01(input.intensity);
    const blast = clamp01(input.blastFraction);
    const weapon = resolveWeapon(intensity, blast);
    const profile = profileArg ?? input.profile ?? BRICK_PROFILE;
    const pos = asTriple(input.position);
    const ix = pos[0];
    const iy = pos[1];
    stamps += 1;
    if (weapon === 'bullet') return applyBullet(ix, iy, energy, profile);
    return applyBlast(ix, iy, energy, weapon, stamps, profile);
  };

  const getStats = (): LedgerStats => {
    const counts = { intact: 0, spalled: 0, cracked: 0, rim: 0, removed: 0 };
    let pocks = 0;
    let integ = 0;
    let crackedJoints = 0;
    let backFace = 0;
    for (const b of bricks) {
      counts[b.state] += 1;
      pocks += b.pockCount;
      integ += b.integrity;
      if (b.backSpall > 0.08) backFace += 1;
    }
    for (const j of joints) if (j.cracked) crackedJoints += 1;
    return {
      ...counts,
      pocks,
      stamps,
      mergedZones: countMergedZones(bricks, grid.neighbors),
      crackedJoints,
      meanIntegrity: bricks.length ? integ / bricks.length : 1,
      backFace,
    };
  };

  const snapshot = (): string => {
    const brickBits = bricks.map((b) =>
      [
        b.state,
        b.integrity.toFixed(4),
        b.pockCount,
        b.pushIn.toFixed(4),
        b.cornerMask,
        b.throughCrack,
        b.edgeDarken.toFixed(3),
        b.backSpall.toFixed(3),
        b.backScorch.toFixed(3),
      ].join(':'),
    );
    const jointBits = joints.map((j) => (j.cracked ? `${j.id}:${j.crackDepth.toFixed(3)}` : '')).filter(Boolean);
    return `${brickBits.join('|')}#${jointBits.join(',')}`;
  };

  const reset = (): void => {
    for (let i = 0; i < bricks.length; i += 1) {
      const fresh = makeBrick(i, grid.bricks[i].seed);
      Object.assign(bricks[i], fresh);
    }
    for (const j of joints) {
      j.cracked = false;
      j.crackDepth = 0;
    }
    paths.length = 0;
    stamps = 0;
  };

  return {
    grid,
    bricks,
    joints,
    stamp,
    reset,
    getStats,
    getCrackPaths: () => paths,
    snapshot,
  };
}

/** Test helper: every consecutive pair on a path shares a lattice vertex. */
export function pathIsStaircase(
  grid: WallGrid,
  path: CrackPath,
): { ok: boolean; alternations: number; sameKindSteps: number } {
  let alternations = 0;
  let sameKindSteps = 0;
  for (let i = 0; i < path.joints.length - 1; i += 1) {
    const a = grid.joints[path.joints[i]];
    const b = grid.joints[path.joints[i + 1]];
    const adjacent = grid.jointAdj[a.id].includes(b.id);
    if (!adjacent) return { ok: false, alternations, sameKindSteps };
    if (a.kind !== b.kind) alternations += 1;
    else sameKindSteps += 1;
  }
  return { ok: true, alternations, sameKindSteps };
}

export { STATE_RANK };
